import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {concat, defer, forkJoin, Observable, of} from 'rxjs';
import {finalize, tap} from 'rxjs/operators';
import {AdminNewsV3RestService} from '../../../services/rest/v3/admin-news-rest.service';
import {
  NEWS_TYPES,
  NewsContent,
  NewsImageItem,
  NewsRecipient,
  NewsRecipientManager,
  NewsRecipientSchoolManagers,
  NewsRecipientStudent,
  NewsRecipientTeacher,
  NewsType,
} from '../../../model/v3/news';
import {SchoolV3RestService} from '../../../services/rest/v3/school-rest.service';
import {Pageable} from '../../../model/rest/base';
import {SchoolListView, V3School, V3SchoolManager, V3SchoolStudent, V3SchoolTeacher} from '../../../model/v3/schools';
import {AdminRestService} from '../../../services/rest/admin-rest.service';
import {
  ONE_SEARCH_TYPES,
  OneSearchAnyEntry,
  OneSearchManagerEntry,
  OneSearchType,
} from '../../../model/v3/one-search';
import {V3Person} from '../../../model/v3/people';
import {FileV3RestService} from '../../../services/rest/v3/file-rest.service';
import {V3FileItem} from '../../../model/v3/file';
import {ModalComponent} from '../../../components/modal/modal.component';

interface SchoolOption {
  id: number;
  name: string;
  country: string;
}

type PersonRole = 'manager' | 'student' | 'teacher';
type SchoolAudienceRole = 'managers' | 'students' | 'teachers';

interface SchoolAudienceSelection {
  schoolId: number;
  role: SchoolAudienceRole;
}

interface PersonOption {
  id: number;
  role: PersonRole;
  name: string;
  surname: string;
  email: string;
  schoolId: number;
  schoolName: string;
}

interface GalleryImage {
  existingId: number | null;
  url: string;
  uploadedFile: V3FileItem | null;
}

interface SelectedRecipientItem {
  id: number;
  type: 'school' | 'person';
  schoolRole?: SchoolAudienceRole;
  personRole?: PersonRole;
  label: string;
  subtitle?: string;
}

interface NewsEditorForm {
  title: string;
  subtitle: string;
  bodyHtml: string;
  imageUrl: string | null;
  labels: string;
  type: NewsType;
  publicationDate: string;
  allManagers: boolean;
  allStudents: boolean;
  allTeachers: boolean;
}

const MAX_GALLERY_IMAGES = 10;

const SCHOOL_AUDIENCE_LABELS: Record<SchoolAudienceRole, string> = {
  managers: 'Managers',
  students: 'Students',
  teachers: 'Teachers',
};

const SCHOOL_AUDIENCE_RECIPIENT_TYPES: Record<SchoolAudienceRole, NewsRecipient['@type']> = {
  managers: 'NewsRecipientSchoolManagers',
  students: 'NewsRecipientSchoolStudents',
  teachers: 'NewsRecipientSchoolTeachers',
};

const SCHOOL_RECIPIENT_AUDIENCES: Record<string, SchoolAudienceRole> = {
  NewsRecipientSchoolManagers: 'managers',
  NewsRecipientSchoolStudents: 'students',
  NewsRecipientSchoolTeachers: 'teachers',
};

const PERSON_ROLE_LABELS: Record<'all' | PersonRole, string> = {
  all: 'All roles',
  manager: 'Managers',
  student: 'Students',
  teacher: 'Teachers',
};

const SEARCH_TYPE_ROLES: Record<string, PersonRole> = {
  [ONE_SEARCH_TYPES.manager]: 'manager',
  [ONE_SEARCH_TYPES.student]: 'student',
  [ONE_SEARCH_TYPES.teacher]: 'teacher',
};

// role relations in these states are not offered as news recipients
const EXCLUDED_ROLE_STATUSES = new Set(['REMOVED', 'BLOCKED']);

// html elements that never have a closing tag, so they never stay on the open-elements stack
const VOID_ELEMENTS = new Set(['img', 'br', 'hr']);

// mutually exclusive class families used by the copy toolbar
const LAYOUT_CLASSES = /^h-(align-(left|center|right)|float-(left|right))$/;
const COPY_COLOR_CLASSES = /^(copy-color-.+|text-(primary|secondary|success|danger|warning|info|muted))$/;

@Component({
  selector: 'app-admin-news-editor-page',
  templateUrl: './admin-news-editor-page.component.html',
  styleUrls: ['./admin-news-editor-page.component.scss'],
  standalone: false,
})
export class AdminNewsEditorPageComponent implements OnInit {
  @ViewChild('bodyInput') bodyInput?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('newsForm') newsForm?: NgForm;
  @ViewChild('linkModal') linkModal?: ModalComponent;

  linkUrl = '';

  readonly newsTypes = NEWS_TYPES;
  readonly maxGalleryImages = MAX_GALLERY_IMAGES;
  // colours available in the copy editor: the brand palette (copy-color-*
  // classes in the global styles) plus the standard bootstrap text-*
  // utilities; 'Default' resets to the standard text colour of the theme.
  // The actual colour values live only in css — the swatches take them
  // from the same classes via currentColor.
  readonly copyColors: { label: string; cssClass: string }[] = [
    { label: 'Default', cssClass: 'copy-color-default' },
    { label: 'Navy', cssClass: 'copy-color-navy' },
    { label: 'Gray', cssClass: 'copy-color-gray' },
    { label: 'Crimson', cssClass: 'copy-color-crimson' },
    { label: 'Red', cssClass: 'copy-color-red' },
    { label: 'Primary', cssClass: 'text-primary' },
    { label: 'Secondary', cssClass: 'text-secondary' },
    { label: 'Success', cssClass: 'text-success' },
    { label: 'Danger', cssClass: 'text-danger' },
    { label: 'Warning', cssClass: 'text-warning' },
    { label: 'Info', cssClass: 'text-info' },
    { label: 'Muted', cssClass: 'text-muted' },
  ];
  readonly schoolAudienceRoles: SchoolAudienceRole[] = ['managers', 'students', 'teachers'];
  readonly personRoleFilters: ('all' | PersonRole)[] = ['all', 'manager', 'student', 'teacher'];

  newsId: number | null = null;
  imagePreviewUrl: string | null = null;
  uploadedImageFile: V3FileItem | null = null;
  private imageDirty = false;
  galleryImages: GalleryImage[] = [];
  private removedGalleryImageIds: number[] = [];
  galleryUploading = false;
  schoolSearchTerm = '';
  personSearchTerm = '';
  recipientPickerTab: 'schools' | 'people' = 'schools';
  personPickerRole: 'all' | PersonRole = 'all';
  personSearchPending = false;
  schoolsById: Record<number, SchoolOption> = {};
  availableSchools: SchoolOption[] = [];
  availablePeople: PersonOption[] = [];
  schoolAudiences: SchoolAudienceSelection[] = [];
  selectedPeople: PersonOption[] = [];
  private personSearchDebounce: ReturnType<typeof setTimeout> | null = null;
  selectedRecipientItems: SelectedRecipientItem[] = [];
  customAudienceMissing = false;
  loading = false;
  form: NewsEditorForm = this.createEmptyForm();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminNewsRest: AdminNewsV3RestService,
    private schoolRest: SchoolV3RestService,
    private adminRest: AdminRestService,
    private fileV3Rest: FileV3RestService,
  ) {
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('newsId');
    this.newsId = id ? +id : null;

    if (!this.newsId) return;

    this.loading = true;
    forkJoin({
      news: this.adminNewsRest.getNewsById(this.newsId),
      recipients: this.loadAllRecipients(this.newsId),
    }).subscribe(({news, recipients}) => {
      const recipientsContent = recipients?.content ?? [];
      this.form = {
        title: news.content?.title || '',
        subtitle: news.content?.subtitle || '',
        bodyHtml: news.content?.copy || '',
        imageUrl: news.content?.image || null,
        labels: news.content?.labels || '',
        type: news.content?.type || 'NEWS',
        publicationDate: this.toDateTimeLocalValue(news.publicationDate),
        // legacy news store "all managers" as an empty recipients list
        allManagers: recipientsContent.length === 0
          || recipientsContent.some((recipient) => recipient['@type'] === 'NewsRecipientAllManagers'),
        allStudents: recipientsContent.some((recipient) => recipient['@type'] === 'NewsRecipientAllStudents'),
        allTeachers: recipientsContent.some((recipient) => recipient['@type'] === 'NewsRecipientAllTeachers'),
      };
      this.imagePreviewUrl = news.content?.image || null;
      this.galleryImages = (news.images ?? [])
        .slice()
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((image) => ({existingId: image.id, url: image.url, uploadedFile: null}));
      this.loadRecipientsState(recipientsContent);
      this.loading = false;
    }, () => {
      this.loading = false;
    });
  }

  get pageTitle(): string {
    return this.newsId ? 'Edit News Item' : 'Create News Item';
  }

  save(): void {
    this.customAudienceMissing = this.buildRecipientsPayload().length === 0;
    if (this.newsForm?.invalid || this.customAudienceMissing) {
      Object.values(this.newsForm?.controls ?? {}).forEach((control) => control.markAsTouched());
      setTimeout(() => {
        document.querySelector('.form-control.is-invalid, .alert-danger')?.scrollIntoView({behavior: 'smooth', block: 'center'});
      });
      return;
    }
    this.form.bodyHtml = this.stripExpiringSignatures(this.form.bodyHtml);
    const content: NewsContent = {
      title: this.form.title,
      subtitle: this.form.subtitle,
      labels: this.form.labels,
      copy: this.form.bodyHtml,
      image: this.imagePreviewUrl || '',
      type: this.form.type,
    };
    this.loading = true;
    if (!this.newsId) {
      this.adminNewsRest.createNews(content).subscribe((created) => {
        this.persistSupplementaryData(created.id);
      }, () => {
        this.loading = false;
      });
      return;
    }
    this.adminNewsRest.updateNewsContent(this.newsId, content).subscribe(() => {
      this.persistSupplementaryData(this.newsId!);
    }, () => {
      this.loading = false;
    });
  }

  get audienceSummary(): string {
    const parts: string[] = [];
    if (this.form.allManagers) parts.push('All managers');
    if (this.form.allStudents) parts.push('All students');
    if (this.form.allTeachers) parts.push('All teachers');
    if (this.selectedRecipientItems.length > 0) {
      parts.push(`${this.selectedRecipientItems.length} selected recipient(s)`);
    }
    return parts.length > 0 ? parts.join(', ') : 'No audience selected';
  }

  selectRecipientPickerTab(tab: 'schools' | 'people'): void {
    this.recipientPickerTab = tab;
    if (tab === 'people') {
      this.searchPeople();
    }
  }

  selectPersonPickerRole(role: 'all' | PersonRole): void {
    this.personPickerRole = role;
    this.searchPeople();
  }

  getPersonRoleLabel(role: 'all' | PersonRole): string {
    return PERSON_ROLE_LABELS[role];
  }

  getSchoolAudienceLabel(role: SchoolAudienceRole): string {
    return SCHOOL_AUDIENCE_LABELS[role];
  }

  hasSchoolAudience(school: SchoolOption, role: SchoolAudienceRole): boolean {
    return this.schoolAudiences.some((entry) => entry.schoolId === school.id && entry.role === role);
  }

  addSchoolAudience(school: SchoolOption, role: SchoolAudienceRole): void {
    if (this.hasSchoolAudience(school, role)) {
      return;
    }
    this.schoolsById[school.id] = school;
    this.schoolAudiences = [...this.schoolAudiences, {schoolId: school.id, role}];
    this.customAudienceMissing = false;
    this.rebuildSelectedRecipientItems();
  }

  removeSchoolAudience(schoolId: number, role: SchoolAudienceRole): void {
    this.schoolAudiences = this.schoolAudiences.filter((entry) => !(entry.schoolId === schoolId && entry.role === role));
    this.rebuildSelectedRecipientItems();
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.fileV3Rest.uploadFile(file).subscribe((uploadEvent) => {
      if (uploadEvent.type === HttpEventType.UploadProgress) {
        return;
      }
      if (uploadEvent instanceof HttpResponse && uploadEvent.body) {
        const fileItem = uploadEvent.body as V3FileItem;
        const fullUrl = fileItem.publicAccessUrl || null;
        this.uploadedImageFile = fileItem;
        this.imageDirty = true;
        this.imagePreviewUrl = fullUrl;
        this.form.imageUrl = fullUrl;
      }
    });
  }

  removeImage(): void {
    this.uploadedImageFile = null;
    this.imageDirty = true;
    this.imagePreviewUrl = null;
    this.form.imageUrl = null;
  }

  get canAddGalleryImage(): boolean {
    return this.galleryImages.length < MAX_GALLERY_IMAGES;
  }

  onGalleryImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) {
      return;
    }
    const remainingSlots = MAX_GALLERY_IMAGES - this.galleryImages.length;
    const accepted = files.slice(0, remainingSlots);
    if (!accepted.length) {
      return;
    }
    this.galleryUploading = true;
    let pending = accepted.length;
    accepted.forEach((file) => {
      this.fileV3Rest.uploadFile(file).subscribe((uploadEvent) => {
        if (uploadEvent.type === HttpEventType.UploadProgress) {
          return;
        }
        if (uploadEvent instanceof HttpResponse && uploadEvent.body) {
          const fileItem = uploadEvent.body as V3FileItem;
          if (fileItem.publicAccessUrl && this.galleryImages.length < MAX_GALLERY_IMAGES) {
            this.galleryImages = [...this.galleryImages, {
              existingId: null,
              url: fileItem.publicAccessUrl,
              uploadedFile: fileItem,
            }];
          }
          pending -= 1;
          this.galleryUploading = pending > 0;
        }
      }, () => {
        pending -= 1;
        this.galleryUploading = pending > 0;
      });
    });
  }

  removeGalleryImage(index: number): void {
    const image = this.galleryImages[index];
    if (!image) {
      return;
    }
    if (image.existingId) {
      this.removedGalleryImageIds = [...this.removedGalleryImageIds, image.existingId];
    }
    this.galleryImages = this.galleryImages.filter((_, i) => i !== index);
  }

  // swaps a body image with the current main image (the old main
  // image, if any, takes the promoted image's place in the list)
  setAsMainImage(index: number): void {
    const image = this.galleryImages[index];
    if (!image) {
      return;
    }
    const oldMainUrl = this.imagePreviewUrl;
    const oldMainFile = this.uploadedImageFile;

    if (image.existingId) {
      this.removedGalleryImageIds = [...this.removedGalleryImageIds, image.existingId];
    }
    const reordered = this.galleryImages.filter((_, i) => i !== index);
    if (oldMainUrl) {
      reordered.splice(index, 0, {
        existingId: null,
        url: oldMainUrl,
        uploadedFile: oldMainFile ?? this.urlOnlyFileItem(oldMainUrl),
      });
    }
    this.galleryImages = reordered;

    this.imagePreviewUrl = image.url;
    this.form.imageUrl = image.url;
    this.uploadedImageFile = image.uploadedFile ?? this.urlOnlyFileItem(image.url);
    this.imageDirty = true;
  }

  moveMainImageToGallery(): void {
    if (!this.imagePreviewUrl || !this.canAddGalleryImage) {
      return;
    }
    this.galleryImages = [...this.galleryImages, {
      existingId: null,
      url: this.imagePreviewUrl,
      uploadedFile: this.uploadedImageFile ?? this.urlOnlyFileItem(this.imagePreviewUrl),
    }];
    this.removeImage();
  }

  // the backend accepts a file reference without an id when the URL is already public
  private urlOnlyFileItem(url: string): V3FileItem {
    return {publicAccessUrl: url} as V3FileItem;
  }

  moveGalleryImage(index: number, offset: number): void {
    const target = index + offset;
    if (target < 0 || target >= this.galleryImages.length) {
      return;
    }
    const reordered = [...this.galleryImages];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    this.galleryImages = reordered;
  }

  // an uploaded file is previewed through a presigned s3 link which expires
  // after 10 hours; the saved copy must reference the permanent public url,
  // i.e. the same location with the signature query stripped (the file is
  // made public when the news images are saved)
  private stripExpiringSignatures(copy: string): string {
    return copy.replace(/(src="[^"?]+)\?[^"]*X-Amz-Signature[^"]*(")/gi, '$1$2');
  }

  insertGalleryImageIntoBody(image: GalleryImage): void {
    const snippet = `<img src="${image.url}" alt="" />`;
    const textarea = this.bodyInput?.nativeElement;
    const value = this.form.bodyHtml;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    this.form.bodyHtml = `${value.slice(0, start)}${snippet}${value.slice(end)}`;
    textarea?.focus();
  }

  applyWrap(before: string, after: string = before): void {
    const textarea = this.bodyInput?.nativeElement;
    if (!textarea) {
      return;
    }

    const value = this.form.bodyHtml;
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const selected = value.slice(start, end);
    const wrapped = `${before}${selected || 'text'}${after}`;

    this.form.bodyHtml = `${value.slice(0, start)}${wrapped}${value.slice(end)}`;
    textarea.focus();
  }

  applyParagraph(): void {
    this.applyWrap('<p>', '</p>');
  }

  // bold/italic/underline as bootstrap utility classes, so they also work on
  // elements with their own font styling (e.g. headings): a non-empty selection
  // is wrapped in a styled span, a bare caret toggles the class on the element
  // it points at
  applyTextStyle(styleClass: string): void {
    const textarea = this.bodyInput?.nativeElement;
    if (!textarea) {
      return;
    }
    const value = this.form.bodyHtml;
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const tag = start === end ? this.findTargetTag(value, start) : null;
    if (!tag) {
      this.applyWrap(`<span class="${styleClass}">`, '</span>');
      return;
    }
    this.form.bodyHtml = value.slice(0, tag.start)
      + this.withToggledClass(value.slice(tag.start, tag.end), styleClass)
      + value.slice(tag.end);
    textarea.focus();
  }

  // heading levels sized in .news-copy, on top of the normal body text
  applyHeading(level: 1 | 2 | 3 | 4 | 5 | 6): void {
    this.applyWrap(`<h${level}>`, `</h${level}>`);
  }

  // colour class shown as the bar under the "A" on the dropdown toggle
  lastCopyColorClass = 'copy-color-default';

  // a non-empty selection is wrapped in a coloured span; a bare caret sets the
  // colour class on the element it points at (e.g. a whole heading), replacing
  // any colour class already present
  applyColor(color: { label: string; cssClass: string }): void {
    const textarea = this.bodyInput?.nativeElement;
    if (!textarea) {
      return;
    }
    this.lastCopyColorClass = color.cssClass;
    const value = this.form.bodyHtml;
    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? value.length;
    const tag = start === end ? this.findTargetTag(value, start) : null;
    if (!tag) {
      this.applyWrap(`<span class="${color.cssClass}">`, '</span>');
      return;
    }
    this.form.bodyHtml = value.slice(0, tag.start)
      + this.withExclusiveClass(value.slice(tag.start, tag.end), color.cssClass, COPY_COLOR_CLASSES)
      + value.slice(tag.end);
    textarea.focus();
  }

  // static bootstrap callout box around the selection
  applyCallout(variant: 'info' | 'warning' | 'danger'): void {
    this.applyWrap(`<div class="alert alert-${variant}">`, '</div>');
  }

  insertSeparator(): void {
    const textarea = this.bodyInput?.nativeElement;
    const value = this.form.bodyHtml;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    this.form.bodyHtml = `${value.slice(0, start)}<hr />${value.slice(end)}`;
    textarea?.focus();
  }

  applyBulletList(): void {
    this.applyWrap('<ul><li>', '</li></ul>');
  }

  applyNumberedList(): void {
    this.applyWrap('<ol><li>', '</li></ol>');
  }

  // asks for the url first; the tag is only inserted once the url is confirmed
  applyLink(): void {
    this.linkUrl = 'https://';
    this.linkModal?.show();
  }

  insertLink(): void {
    const url = this.linkUrl.trim();
    if (!url) {
      return;
    }
    this.linkModal?.hide();
    const textarea = this.bodyInput?.nativeElement;
    const value = this.form.bodyHtml;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    if (start === end) {
      // nothing selected: the url doubles as the link text
      this.form.bodyHtml = `${value.slice(0, start)}<a href="${url}">${url}</a>${value.slice(end)}`;
      textarea?.focus();
      return;
    }
    this.applyWrap(`<a href="${url}">`, '</a>');
  }

  // sets an h-align-* class on the element the caret points at (e.g. an inserted
  // <img>); with no element around the caret it wraps the selection in an aligned paragraph
  applyAlign(align: 'left' | 'center' | 'right'): void {
    const textarea = this.bodyInput?.nativeElement;
    if (!textarea) {
      return;
    }
    const value = this.form.bodyHtml;
    const caret = textarea.selectionStart ?? value.length;
    const tag = this.findTargetTag(value, caret);
    if (!tag) {
      this.applyWrap(`<p class="h-align-${align}">`, '</p>');
      return;
    }
    this.form.bodyHtml = value.slice(0, tag.start)
      + this.withExclusiveClass(value.slice(tag.start, tag.end), `h-align-${align}`, LAYOUT_CLASSES)
      + value.slice(tag.end);
    textarea.focus();
  }

  // floats the image the caret points at, so the following text wraps around it
  // and takes the full width again once the image ends
  applyImageWrap(side: 'left' | 'right'): void {
    const textarea = this.bodyInput?.nativeElement;
    if (!textarea) {
      return;
    }
    const value = this.form.bodyHtml;
    const caret = textarea.selectionStart ?? value.length;
    const tag = this.findTargetTag(value, caret);
    if (!tag) {
      return;
    }
    this.form.bodyHtml = value.slice(0, tag.start)
      + this.withExclusiveClass(value.slice(tag.start, tag.end), `h-float-${side}`, LAYOUT_CLASSES)
      + value.slice(tag.end);
    textarea.focus();
  }

  // finds the opening tag to style: the tag under (or right behind) the caret,
  // or the innermost element still open at the caret position
  private findTargetTag(value: string, caret: number): {start: number; end: number} | null {
    const tagPattern = /<(\/?)([a-z][\w-]*)(?:"[^"]*"|'[^']*'|[^>"'])*?(\/?)>/gi;
    const openTags: {name: string; start: number; end: number}[] = [];
    let match: RegExpExecArray | null;
    while ((match = tagPattern.exec(value)) !== null) {
      const [full, closing, rawName, selfClosing] = match;
      const name = rawName.toLowerCase();
      const start = match.index;
      const end = start + full.length;
      if (start >= caret) {
        break;
      }
      if (caret <= end) {
        // the caret is on this tag; for a closing tag fall back to the enclosing element
        if (!closing) {
          return {start, end};
        }
        break;
      }
      if (closing) {
        const openIndex = openTags.map((tag) => tag.name).lastIndexOf(name);
        if (openIndex >= 0) {
          // improperly nested tags opened after the matched one are treated as closed too
          openTags.splice(openIndex);
        }
      } else if (!selfClosing && !VOID_ELEMENTS.has(name)) {
        openTags.push({name, start, end});
      }
    }
    return openTags.pop() ?? null;
  }

  // adds the class to the tag, replacing any class from the same mutually
  // exclusive family (e.g. alignment/float, or the copy colours)
  private withExclusiveClass(tag: string, newClass: string, family: RegExp): string {
    const classAttr = /\s+class\s*=\s*("([^"]*)"|'([^']*)')/i.exec(tag);
    if (!classAttr) {
      return tag.replace(/\s*(\/?)>$/, ` class="${newClass}"$1>`);
    }
    const classes = (classAttr[2] ?? classAttr[3] ?? '')
      .split(/\s+/)
      .filter((cls) => cls && !family.test(cls));
    classes.push(newClass);
    return tag.slice(0, classAttr.index)
      + ` class="${classes.join(' ')}"`
      + tag.slice(classAttr.index + classAttr[0].length);
  }

  // adds the class to the tag, or removes it when already present
  private withToggledClass(tag: string, toggledClass: string): string {
    const classAttr = /\s+class\s*=\s*("([^"]*)"|'([^']*)')/i.exec(tag);
    if (!classAttr) {
      return tag.replace(/\s*(\/?)>$/, ` class="${toggledClass}"$1>`);
    }
    const classes = (classAttr[2] ?? classAttr[3] ?? '').split(/\s+/).filter(Boolean);
    const index = classes.indexOf(toggledClass);
    if (index >= 0) {
      classes.splice(index, 1);
    } else {
      classes.push(toggledClass);
    }
    return tag.slice(0, classAttr.index)
      + (classes.length ? ` class="${classes.join(' ')}"` : '')
      + tag.slice(classAttr.index + classAttr[0].length);
  }

  schoolSearchUpdated(): void {
    const search = this.schoolSearchTerm.trim();
    if (!search || search.length < 2) {
      this.availableSchools = [];
      return;
    }
    this.schoolRest.listSchoolsListView({
      name: search,
      pageable: new Pageable(0, 20, []),
    }).subscribe((page) => {
      const options = (page?.content ?? []).map((school) => this.toSchoolOption(school));
      options.forEach((school) => {
        this.schoolsById[school.id] = school;
      });
      this.availableSchools = options;
    });
  }

  personSearchUpdated(): void {
    if (this.personSearchDebounce) {
      clearTimeout(this.personSearchDebounce);
    }
    this.personSearchDebounce = setTimeout(() => this.searchPeople(), 300);
  }

  addPerson(person: PersonOption): void {
    if (this.isPersonSelected(person)) {
      return;
    }
    this.selectedPeople = [...this.selectedPeople, person];
    this.availablePeople = this.availablePeople.filter((it) => !this.samePerson(it, person));
    this.customAudienceMissing = false;
    this.rebuildSelectedRecipientItems();
  }

  removePerson(person: PersonOption): void {
    this.selectedPeople = this.selectedPeople.filter((it) => !this.samePerson(it, person));
    this.rebuildSelectedRecipientItems();
  }

  removeSelectedRecipient(item: SelectedRecipientItem): void {
    if (item.type === 'school' && item.schoolRole) {
      this.removeSchoolAudience(item.id, item.schoolRole);
      return;
    }
    const person = this.selectedPeople.find((it) => it.id === item.id && it.role === item.personRole);
    if (person) {
      this.removePerson(person);
    }
  }

  private createEmptyForm(): NewsEditorForm {
    return {
      title: '',
      subtitle: '',
      bodyHtml: '',
      imageUrl: null,
      labels: '',
      type: 'NEWS',
      publicationDate: '',
      allManagers: false,
      allStudents: false,
      allTeachers: false,
    };
  }

  private persistSupplementaryData(targetNewsId: number): void {
    const requests: Observable<unknown>[] = [];
    if (this.form.publicationDate) {
      requests.push(this.adminNewsRest.updatePublicationDate(targetNewsId, this.form.publicationDate));
    }
    if (this.imageDirty) {
      requests.push(this.adminNewsRest.updateNewsImage(targetNewsId, this.uploadedImageFile));
    }
    requests.push(...this.buildGalleryRequests(targetNewsId));
    requests.push(this.adminNewsRest.updateRecipients(targetNewsId, this.buildRecipientsPayload()));
    // sequential on purpose: the image and content updates rewrite the whole news
    // entity, so concurrent requests would overwrite each other's changes
    concat(...requests).pipe(
      finalize(() => this.loading = false)
    ).subscribe({
      complete: () => {
        this.router.navigate(['/admin/news', targetNewsId]).then();
      },
      error: () => {
        this.router.navigate(['/admin/news', targetNewsId]).then();
      },
    });
  }

  private buildGalleryRequests(targetNewsId: number): Observable<unknown>[] {
    const requests: Observable<unknown>[] = [];
    this.galleryImages
      .filter((image) => !image.existingId && image.uploadedFile)
      .forEach((image) => {
        requests.push(this.adminNewsRest.addNewsImage(targetNewsId, image.uploadedFile!).pipe(
          tap((created: NewsImageItem) => {
            image.existingId = created.id;
          }),
        ));
      });
    this.removedGalleryImageIds.forEach((imageId) => {
      requests.push(this.adminNewsRest.deleteNewsImage(targetNewsId, imageId));
    });
    // deferred so the ids assigned by the create requests above are visible
    requests.push(defer(() => {
      const orderedIds = this.galleryImages
        .map((image) => image.existingId)
        .filter((imageId): imageId is number => !!imageId);
      return orderedIds.length > 0
        ? this.adminNewsRest.updateNewsImagesOrder(targetNewsId, orderedIds)
        : of(null);
    }));
    return requests;
  }

  private buildRecipientsPayload(): NewsRecipient[] {
    const recipients: NewsRecipient[] = [];
    if (this.form.allManagers) {
      recipients.push({'@type': 'NewsRecipientAllManagers'});
    }
    if (this.form.allStudents) {
      recipients.push({'@type': 'NewsRecipientAllStudents'});
    }
    if (this.form.allTeachers) {
      recipients.push({'@type': 'NewsRecipientAllTeachers'});
    }
    this.selectedPeople.forEach((person) => {
      const ref = {id: person.id};
      recipients.push((person.role === 'manager'
        ? {'@type': 'NewsRecipientManager', manager: ref as V3SchoolManager}
        : person.role === 'student'
          ? {'@type': 'NewsRecipientStudent', student: ref as V3SchoolStudent}
          : {'@type': 'NewsRecipientTeacher', teacher: ref as V3SchoolTeacher}) as NewsRecipient);
    });
    this.schoolAudiences.forEach((entry) => {
      recipients.push({
        '@type': SCHOOL_AUDIENCE_RECIPIENT_TYPES[entry.role],
        learningUnit: {id: entry.schoolId} as V3School,
      } as NewsRecipient);
    });
    return recipients;
  }

  private loadAllRecipients(newsId: number): ReturnType<AdminNewsV3RestService['listRecipients']> {
    return this.adminNewsRest.listRecipients(newsId, new Pageable(0, 1000, []));
  }

  private loadRecipientsState(recipients: NewsRecipient[]): void {
    this.schoolAudiences = [];
    this.selectedPeople = [];
    recipients.forEach((recipient) => {
      const schoolRole = SCHOOL_RECIPIENT_AUDIENCES[recipient['@type']];
      if (schoolRole) {
        this.registerSchoolAudience((recipient as NewsRecipientSchoolManagers).learningUnit, schoolRole);
        return;
      }
      const person = this.recipientPersonOption(recipient);
      if (person) {
        this.selectedPeople.push(person);
      }
    });
    this.rebuildSelectedRecipientItems();
    this.loadMissingSchoolNames();
  }

  private recipientPersonOption(recipient: NewsRecipient): PersonOption | null {
    switch (recipient['@type']) {
      case 'NewsRecipientManager': {
        const manager = (recipient as NewsRecipientManager).manager;
        return this.toPersonOption(manager?.id, 'manager', manager?.person, manager?.school);
      }
      case 'NewsRecipientStudent': {
        const student = (recipient as NewsRecipientStudent).student;
        return this.toPersonOption(student?.id, 'student', student?.person, student?.school);
      }
      case 'NewsRecipientTeacher': {
        const teacher = (recipient as NewsRecipientTeacher).teacher;
        return this.toPersonOption(teacher?.id, 'teacher', teacher?.person, teacher?.school);
      }
      default:
        return null;
    }
  }

  private registerSchoolAudience(school: V3School | undefined, role: SchoolAudienceRole): void {
    const schoolId = school?.id;
    if (!schoolId) {
      return;
    }
    const name = school?.details?.name;
    if (name) {
      this.schoolsById[schoolId] = this.schoolsById[schoolId] || {id: schoolId, name, country: ''};
    }
    this.schoolAudiences.push({schoolId, role});
  }

  private toDateTimeLocalValue(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  }

  private toSchoolOption(school: SchoolListView): SchoolOption {
    const country = school.address?.country || '';
    return {
      id: school.id,
      name: school.details?.name || `School ${school.id}`,
      country,
    };
  }

  private searchPeople(): void {
    const term = this.personSearchTerm.trim();
    if (term.length < 3) {
      this.availablePeople = [];
      this.personSearchPending = false;
      return;
    }
    this.personSearchPending = true;
    this.adminRest.searchDashboardTable(term, new Pageable(0, 15, []), this.searchTypesForRole()).subscribe((page) => {
      this.personSearchPending = false;
      this.availablePeople = (page?.content ?? [])
        .map((entry) => this.toPersonOptionFromSearchEntry(entry))
        .filter((option): option is PersonOption => !!option && !this.isPersonSelected(option));
    }, () => {
      this.personSearchPending = false;
      this.availablePeople = [];
    });
  }

  private searchTypesForRole(): OneSearchType[] {
    const allTypes = Object.keys(SEARCH_TYPE_ROLES) as OneSearchType[];
    return this.personPickerRole === 'all'
      ? allTypes
      : allTypes.filter((type) => SEARCH_TYPE_ROLES[type] === this.personPickerRole);
  }

  private toPersonOptionFromSearchEntry(entry: OneSearchAnyEntry): PersonOption | null {
    const role = SEARCH_TYPE_ROLES[entry['@type']];
    if (!role) {
      return null;
    }
    // manager/student/teacher search entries share this shape
    const personEntry = entry as OneSearchManagerEntry & {status?: unknown};
    if (personEntry.status && EXCLUDED_ROLE_STATUSES.has(`${personEntry.status}`)) {
      return null;
    }
    return this.toPersonOption(personEntry.id, role, personEntry.person, personEntry.school);
  }

  private toPersonOption(
    id: number | undefined,
    role: PersonRole,
    person: V3Person | undefined,
    school: {id?: number; details?: {name?: string}} | undefined,
  ): PersonOption | null {
    if (!id) {
      return null;
    }
    const schoolId = school?.id || 0;
    const schoolName = school?.details?.name || (schoolId ? this.schoolsById[schoolId]?.name || '' : '');
    if (schoolId && schoolName) {
      this.schoolsById[schoolId] = this.schoolsById[schoolId] || {id: schoolId, name: schoolName, country: ''};
    }
    return {
      id,
      role,
      name: person?.details?.name || '',
      surname: person?.details?.surname || '',
      email: person?.registrationEmail || '',
      schoolId,
      schoolName,
    };
  }

  private samePerson(a: {id: number; role: PersonRole}, b: {id: number; role: PersonRole}): boolean {
    return a.id === b.id && a.role === b.role;
  }

  private isPersonSelected(option: PersonOption): boolean {
    return this.selectedPeople.some((it) => this.samePerson(it, option));
  }

  private loadMissingSchoolNames(): void {
    const referencedSchoolIds = [
      ...this.schoolAudiences.map((entry) => entry.schoolId),
      ...this.selectedPeople.map((person) => person.schoolId),
    ];
    const missingSchoolIds = Array.from(new Set(
      referencedSchoolIds.filter((schoolId) => !!schoolId && !this.schoolsById[schoolId]),
    ));
    if (!missingSchoolIds.length) {
      return;
    }
    this.schoolRest.listSchoolsListView({
      ids: missingSchoolIds,
      pageable: new Pageable(0, missingSchoolIds.length, []),
    }).subscribe((page) => {
      (page?.content ?? []).forEach((school) => {
        const option = this.toSchoolOption(school);
        this.schoolsById[option.id] = option;
      });
      this.selectedPeople = this.selectedPeople.map((person) => {
        if (!person.schoolId || person.schoolName) {
          return person;
        }
        return {
          ...person,
          schoolName: this.schoolsById[person.schoolId]?.name || person.schoolName,
        };
      });
      this.rebuildSelectedRecipientItems();
    });
  }

  private rebuildSelectedRecipientItems(): void {
    const schools: SelectedRecipientItem[] = this.schoolAudiences.map((entry) => ({
      id: entry.schoolId,
      type: 'school',
      schoolRole: entry.role,
      label: this.schoolsById[entry.schoolId]?.name || `School ${entry.schoolId}`,
      subtitle: SCHOOL_AUDIENCE_LABELS[entry.role],
    }));
    const people: SelectedRecipientItem[] = this.selectedPeople.map((person) => ({
      id: person.id,
      type: 'person',
      personRole: person.role,
      label: `${person.name} ${person.surname}`.trim(),
      subtitle: person.schoolName || undefined,
    }));
    this.selectedRecipientItems = [...schools, ...people];
  }
}
